"use client";
import { icon, parse } from "@fortawesome/fontawesome-svg-core";
import React from "react";
import convert from "./utils/converter"; // Added import for 'convertCurry'
import log from "./utils/logger";
import normalizeIconArgs from "./utils/normalize-icon-args";
import objectWithKey from "./utils/object-with-key";

// Import classList function from the appropriate location
import classList from "./utils/get-class-list-from-props";

export type FontAwesomeIconProps = Partial<{
  beat: boolean;
  border: boolean;
  beatFade: boolean;
  bounce: boolean;
  className: string;
  fade: boolean;
  flash: boolean | "horizontal" | "vertical" | "both";
  mask: object | [] | string;
  maskId: string;
  fixedWidth: boolean;
  inverse: boolean;
  flip: any;
  icon: object | [] | string;
  listItem: boolean;
  pull: "right" | "left";
  pulse: boolean;
  rotation: 0 | 90 | 180 | 270;
  shake: boolean;
  size:
    | "2xs"
    | "xs"
    | "sm"
    | "lg"
    | "xl"
    | "2xl"
    | "1x"
    | "2x"
    | "3x"
    | "4x"
    | "5x"
    | "6x"
    | "7x"
    | "8x"
    | "9x"
    | "10x";
  spin: boolean;
  spinPulse: boolean;
  spinReverse: boolean;
  symbol: boolean | string;
  title: string;
  titleId: string;
  transform: string | object;
  swapOpacity: boolean;
  width?: number;
  height?: number;
}>;

const convertCurry = convert.bind(null, React.createElement);

const FontAwesomeIcon = React.forwardRef(
  (props: FontAwesomeIconProps, ref: React.Ref<unknown>) => {
    const {
      icon: iconArgs,
      mask: maskArgs,
      symbol,
      className = "", // Set a default value for className
      title,
      titleId,
      maskId,
      ...restProps // Capture any other props
    } = props;

    let iconLookup = normalizeIconArgs(iconArgs);

    // Convert boolean props to strings
    const booleanPropToString = (
      propName: string,
      propValue: boolean | string
    ) => {
      return typeof propValue === "boolean" ? propValue.toString() : propValue;
    };

    const classes = objectWithKey("classes", [
      ...(classList(props) || []), // Use the spread operator with nullish coalescing
      ...className.split(" "),
    ]);
    const transform = objectWithKey(
      "transform",
      typeof props.transform === "string"
        ? parse.transform(props.transform)
        : props.transform
    );
    const mask = objectWithKey("mask", normalizeIconArgs(maskArgs));

    const renderedIcon = icon(iconLookup, {
      ...classes,
      ...transform,
      ...mask,
      symbol,
      title,
      titleId,
      maskId,
    });

    if (!renderedIcon) {
      log("Could not find icon", iconLookup);
      return null;
    }

    const { abstract } = renderedIcon;
    const extraProps = { ...restProps, ref }; // Use the spread operator

    // Convert boolean props to strings in the extraProps
    Object.keys(restProps).forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(restProps, key)) {
        extraProps[key] = booleanPropToString(key, restProps[key]);
      }
    });

    return convertCurry(abstract[0], extraProps);
  }
);
FontAwesomeIcon.displayName = "FontAwesomeIcon";

export default FontAwesomeIcon;
